import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, FileText, Plus, Trash2, Image as ImageIcon, User } from 'lucide-react';
import { Button, Input, TextArea, SearchSelect } from '../shared';
import DateTimePicker from '../shared/DateTimePicker';
import { useServiceCategories } from '../../services/serviceCategoryService';

const CreateBlogPostModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  post = null, 
  isEdit = false, 
  isLoading = false 
}) => {
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    author_name: '',
    category: '',
    image: '',
    read_time: 5,
    is_featured: false,
    is_published: false,
    published_at: '',
    sort_order: 0,
    tags: []
  });

  const [errors, setErrors] = useState({});
  const [tagInput, setTagInput] = useState('');

  // Fetch active service categories
  const { data: categoriesResponse, isLoading: categoriesLoading } = useServiceCategories({
    active: true,
    sort_by: 'sort_order',
    sort_order: 'asc',
    per_page: 100
  });

  const categoryOptions = useMemo(() => {
    const categories = categoriesResponse?.data?.data || categoriesResponse?.data || [];
    return categories.map(category => ({
      value: category.category_name,
      label: category.category_name
    }));
  }, [categoriesResponse]);

  useEffect(() => {
    if (isEdit && post) {
      setFormData({
        title: post.title || '',
        slug: post.slug || '',
        excerpt: post.excerpt || '',
        content: post.content || '',
        author_name: post.author_name || '',
        category: post.category || '',
        image: post.image || '',
        read_time: post.read_time || 5,
        is_featured: post.is_featured || false,
        is_published: post.is_published || false,
        published_at: post.published_at ? new Date(post.published_at).toISOString().slice(0, 16) : '',
        sort_order: post.sort_order || 0,
        tags: post.tags || []
      });
    } else {
      setFormData({
        title: '',
        slug: '',
        excerpt: '',
        content: '',
        author_name: '',
        category: '',
        image: '',
        read_time: 5,
        is_featured: false,
        is_published: false,
        published_at: '',
        sort_order: 0,
        tags: []
      });
    }
    setErrors({});
    setTagInput('');
  }, [isEdit, post, isOpen]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Auto-generate slug from title
    if (name === 'title' && !isEdit) {
      const slug = value.toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
      setFormData(prev => ({
        ...prev,
        slug: slug
      }));
    }
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (index) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter((_, i) => i !== index)
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.slug.trim()) {
      newErrors.slug = 'Slug is required';
    }

    if (!formData.excerpt.trim()) {
      newErrors.excerpt = 'Excerpt is required';
    } else if (formData.excerpt.length > 500) {
      newErrors.excerpt = 'Excerpt must be 500 characters or less';
    }

    if (!formData.content.trim()) {
      newErrors.content = 'Content is required';
    }

    if (!formData.author_name.trim()) {
      newErrors.author_name = 'Author name is required';
    }

    if (formData.read_time && (formData.read_time < 1 || formData.read_time > 60)) {
      newErrors.read_time = 'Read time must be between 1 and 60 minutes';
    }

    if (formData.sort_order < 0) {
      newErrors.sort_order = 'Sort order must be a positive number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const submitData = {
      ...formData,
      read_time: parseInt(formData.read_time) || 5,
      sort_order: parseInt(formData.sort_order) || 0,
      published_at: formData.is_published && formData.published_at 
        ? new Date(formData.published_at).toISOString() 
        : (formData.is_published ? new Date().toISOString() : null),
      tags: formData.tags.length > 0 ? formData.tags : null
    };

    onSave(submitData);
  };

  const handleClose = () => {
    setErrors({});
    setTagInput('');
    onClose();
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10001] p-2 sm:p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[95vh] overflow-y-auto relative my-2 sm:my-4">
        {/* Header */}
        <div className="flex items-center justify-between p-3 sm:p-4 md:p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
          <div className="flex items-center">
            <div className="flex-shrink-0 h-6 w-6 sm:h-8 sm:w-8 rounded-full bg-gradient-to-r from-primary-500 to-primary-600 flex items-center justify-center">
              <FileText className="h-3 w-3 sm:h-4 sm:w-4 text-white" />
            </div>
            <h3 className="ml-2 sm:ml-3 text-base sm:text-lg font-medium text-gray-900 truncate">
              {isEdit ? 'Edit Blog Post' : 'Create Blog Post'}
            </h3>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 p-1"
          >
            <X className="h-4 w-4 sm:h-5 sm:w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-3 sm:p-4 md:p-6">
          <div className="space-y-4 sm:space-y-6">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title <span className="text-red-500">*</span>
              </label>
              <Input
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Enter blog post title"
                error={errors.title}
                required
              />
            </div>

            {/* Slug */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Slug <span className="text-red-500">*</span>
              </label>
              <Input
                name="slug"
                value={formData.slug}
                onChange={handleChange}
                placeholder="blog-post-slug"
                error={errors.slug}
                required
              />
              <p className="mt-1 text-xs text-gray-500">URL-friendly version of the title</p>
            </div>

            {/* Author Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Author Name <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  name="author_name"
                  value={formData.author_name}
                  onChange={handleChange}
                  placeholder="Author name"
                  error={errors.author_name}
                  className="pl-10"
                  required
                />
              </div>
            </div>

            {/* Category and Read Time */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <SearchSelect
                  label="Category"
                  value={formData.category}
                  onChange={(value) => {
                    setFormData(prev => ({ ...prev, category: value }));
                    if (errors.category) {
                      setErrors(prev => ({ ...prev, category: '' }));
                    }
                  }}
                  options={categoryOptions}
                  placeholder="Select a category"
                  error={errors.category}
                  searchable={true}
                  loading={categoriesLoading}
                  emptyMessage="No categories found"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Read Time (minutes)
                </label>
                <Input
                  name="read_time"
                  type="number"
                  value={formData.read_time}
                  onChange={handleChange}
                  placeholder="5"
                  min="1"
                  max="60"
                  error={errors.read_time}
                />
              </div>
            </div>

            {/* Excerpt */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Excerpt <span className="text-red-500">*</span>
              </label>
              <TextArea
                name="excerpt"
                value={formData.excerpt}
                onChange={handleChange}
                placeholder="Brief summary of the blog post (max 500 characters)"
                rows={3}
                maxLength={500}
                error={errors.excerpt}
                required
              />
              <p className="mt-1 text-xs text-gray-500">
                {formData.excerpt.length}/500 characters
              </p>
            </div>

            {/* Content */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Content <span className="text-red-500">*</span>
              </label>
              <TextArea
                name="content"
                value={formData.content}
                onChange={handleChange}
                placeholder="Write your blog post content here..."
                rows={12}
                error={errors.content}
                required
              />
            </div>

            {/* Image URL */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Featured Image URL
              </label>
              <div className="relative">
                <ImageIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  name="image"
                  value={formData.image}
                  onChange={handleChange}
                  placeholder="https://example.com/image.jpg"
                  error={errors.image}
                  className="pl-10"
                  type="url"
                />
              </div>
              {formData.image && (
                <div className="mt-2">
                  <img
                    src={formData.image}
                    alt="Preview"
                    className="w-full h-48 object-cover rounded-lg border border-gray-200"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}
            </div>

            {/* Tags */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tags
              </label>
              <div className="flex gap-2 mb-2">
                <Input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  placeholder="Add a tag"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddTag();
                    }
                  }}
                  className="flex-1"
                />
                <Button
                  type="button"
                  onClick={handleAddTag}
                  variant="outline"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {formData.tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {formData.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(index)}
                        className="hover:text-red-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Published Date and Sort Order */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <DateTimePicker
                  label="Published Date & Time"
                  value={formData.published_at}
                  onChange={(value) => {
                    const dateStr = value ? new Date(value).toISOString().slice(0, 16) : '';
                    setFormData(prev => ({ ...prev, published_at: dateStr }));
                    if (errors.published_at) {
                      setErrors(prev => ({ ...prev, published_at: '' }));
                    }
                  }}
                  placeholder="Select date and time"
                  allowPastDates={true}
                  error={errors.published_at}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sort Order
                </label>
                <Input
                  name="sort_order"
                  type="number"
                  value={formData.sort_order}
                  onChange={handleChange}
                  placeholder="0"
                  min="0"
                  error={errors.sort_order}
                />
              </div>
            </div>

            {/* Checkboxes */}
            <div className="space-y-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="is_featured"
                  checked={formData.is_featured}
                  onChange={handleChange}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">Featured Post</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="is_published"
                  checked={formData.is_published}
                  onChange={handleChange}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">Published</span>
              </label>
            </div>
          </div>

          {/* Footer */}
          <div className="flex flex-col sm:flex-row justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
            <Button
              type="button"
              onClick={handleClose}
              variant="outline"
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={isLoading}
              className="w-full sm:w-auto"
            >
              {isEdit ? 'Update Post' : 'Create Post'}
            </Button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default CreateBlogPostModal;

